import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { MuiTelInput } from "mui-tel-input";

import useApi from "../../hooks/useApi";
import useAuth from "../../hooks/useAuth";

import {
    getFieldMessages,
    getFieldMessagesNameOnlyArray,
} from "../../utils/ResponseValidate";
import { fieldNameIndex, stringifyValues } from "../../utils/formValues";
import { getCountryCode } from "../../utils/getCountryCode";

import CustomField from "../Settings/WebForms/components/customField";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { isMobile } from 'react-device-detect';

const WebFormsContent = () => {
    const { t, i18n } = useTranslation();

    const { cleanup } = useAuth();
    const [loading, setLoading] = useState(true);
    const { token } = useParams();
    const { apiUrl, apiInit, csrfCookie, csrfHeader } = useApi();

    const [submitted, setSubmitted] = useState(false);
    const [webForm, setWebForm] = useState();

    const [formValues, setFormValues] = useState({
        token: {
            value: token,
            error: false,
            errorMessage: "",
        },
        name: {
            value: "",
            error: false,
            errorMessage: "",
        },
        company_name: {
            value: "",
            error: false,
            errorMessage: "",
        },
        email: {
            value: "",
            error: false,
            errorMessage: "",
        },
        phone: {
            value: "",
            error: false,
            errorMessage: "",
        },
        role: {
            value: "",
            error: false,
            errorMessage: "",
        },
        contact_source: {
            value: "",
            error: false,
            errorMessage: "",
        },
        custom_field: {},
        origin: {
            value: "",
            error: false,
            errorMessage: "",
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: { ...formValues[name], value },
        });
    };

    const handleChangePhone = (value) => {
        setFormValues({
            ...formValues,
            phone: { ...formValues.phone, value },
        });
    };

    const handleChangeCustomField = (e) => {
        const { name, value, checked, type } = e.target;
        const { fieldIndex } = fieldNameIndex(name);

        setFormValues((prevFormValues) => ({
            ...prevFormValues,
            custom_field: {
                ...prevFormValues.custom_field,
                [fieldIndex]: {
                    ...prevFormValues.custom_field[fieldIndex],
                    value: type === "checkbox" ? checked : value,
                },
            },
        }));
    };

    useEffect(() => {
        setLoading(true);

        fetch(apiUrl(`web-forms/token/${token}`), apiInit())
            .then((response) => {
                if (response.status === 401) {
                    cleanup();
                }

                if (response.status === 200) {
                    response.json().then((data) => {
                        let webForm = data.web_form;

                        setContactSources(data.contact_sources);


                        webForm.fields = [
                            ...webForm.web_form_fields,
                            ...webForm.custom_fields,
                        ].sort((a, b) => {
                            const orderA = a.pivot.order;
                            const orderB = b.pivot.order;
                            return orderA - orderB;
                        });

                        webForm.custom_fields.forEach((customField) => {
                            const customFieldId = customField.id;
                            const customFieldType = customField.type;

                            const createCustomFieldValues = (
                                customFieldType
                            ) => {
                                switch (customFieldType) {
                                    case "multiselect":
                                        return {
                                            value: [],
                                            error: false,
                                            errorMessage: "",
                                        };
                                    case "checkbox":
                                        return {
                                            value: false,
                                            error: false,
                                            errorMessage: "",
                                        };
                                    default:
                                        return {
                                            value: "",
                                            error: false,
                                            errorMessage: "",
                                        };
                                }
                            };

                            setFormValues((prevFormValues) => ({
                                ...prevFormValues,
                                custom_field: {
                                    ...prevFormValues.custom_field,
                                    [customFieldId]:
                                        createCustomFieldValues(
                                            customFieldType
                                        ),
                                },
                            }));
                        });

                        setFormValues((prevFormValues) => ({
                            ...prevFormValues,
                            origin: {
                                ...prevFormValues.origin,
                                value: "form - " + webForm.name,
                            },
                        }));

                        setWebForm(webForm);
                    });
                } else {
                }

                setLoading(false);
            })
            .catch((error) => console.error(error));
    }, [cleanup, apiUrl, apiInit, setLoading, token]);

    const handleSave = async () => {

        setLoading(true);

        await csrfCookie().then((response) => {
            fetch(
                apiUrl(`web-forms/register`),
                apiInit({
                    ...csrfHeader(),
                    method: "POST",
                    body: stringifyValues(formValues),
                })
            )
                .then((response) => {
                    if (response.status === 200) {
                        response.json().then(() => {

                            if (webForm.type === 'open_whatsapp_conversation') {
                                openWhatsappConversation();
                            }

                            if (webForm.submit_type === "redirect") {
                                window.location.replace(
                                    webForm.submit_redirect_url
                                );
                            } else if (webForm.submit_type === "message") {
                                setSubmitted(true);
                            }
                        });
                    } else {
                        response.json().then((data) => {
                            const currentFormValues = Object.keys(formValues);

                            let newFormValues = getFieldMessages(
                                data,
                                currentFormValues,
                                formValues
                            );

                            setFormValues(newFormValues);

                            const currentCustomFieldFormValues = Object.keys({
                                custom_field: { ...formValues.custom_field },
                            });

                            let newCustomFieldFormValues =
                                getFieldMessagesNameOnlyArray(
                                    data,
                                    currentCustomFieldFormValues,
                                    {
                                        custom_field: {
                                            ...formValues.custom_field,
                                        },
                                    }
                                );

                            setFormValues((prevFormValues) => ({
                                ...prevFormValues,
                                ...newCustomFieldFormValues,
                            }));

                            if (response.status !== 422 && data?.message) {
                                alert(data?.message);
                            }

                        });

                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error(error);
                    setLoading(false);
                });
        });
    };

    const openWhatsappConversation = async () => {

        let selectedWhatsappNumber = webForm?.whatsapp_number?.wa_phone_number;

        if (selectedWhatsappNumber) {
            //apenas os números
            selectedWhatsappNumber = selectedWhatsappNumber?.replace(/[^0-9\\.]+/g, '');
        }

        let whatsappMessage = '';

        const formFields = [];

        for (const key in formValues) {

            if (key === 'token' || (!formValues[key].value && key !== 'custom_field')) continue;

            if (key === 'custom_field') {
                for (const field of webForm.custom_fields) {
                    const fieldKey = field.id;

                    if (formValues[key][fieldKey] && formValues[key][fieldKey].value) {
                        formFields.push(`${field.label}=${formValues[key][fieldKey].value}`);
                    }
                }
            } else {
                formFields.push(`${t(key)}: ${formValues[key].value}`);
            }
        }

        whatsappMessage += formFields.join('\n');

        const encodedMessage = encodeURIComponent(whatsappMessage);

        if (isMobile) {
            window.location = `whatsapp://send?phone=${selectedWhatsappNumber}?text=${encodedMessage}`;
        } else {
            const encodedLink = encodeURIComponent(`https://wa.me/${selectedWhatsappNumber}?text=${encodedMessage}`);
            window.open(decodeURIComponent(encodedLink), '_blank');
        }

    };

    const [contactSources, setContactSources] = useState([]);

    if (webForm && webForm.active) {
        return (
            <Box
                bgcolor={
                    webForm.styles.page_background_color ||
                    "var(--background-color-light)"
                }
                color={webForm.styles.font_color}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                minHeight={"100vh"}
            >
                <Box
                    bgcolor={webForm.styles.form_background_color}
                    borderRadius={"8px"}
                    maxWidth={"400px"}
                    width={"70%"}
                    padding={"40px"}
                    height={"min-content"}
                    marginY={8}
                >
                    {webForm.styles.header_image_url && (
                        <Box textAlign={"center"} mb={5}>
                            <img
                                src={webForm.styles.header_image_url}
                                width={"100%"}
                                alt=""
                            />
                        </Box>
                    )}
                    {submitted ? (
                        <Box textAlign={"center"} mb={5}>
                            <Box mt={5}>
                                <h1>{webForm.submit_message}</h1>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Box mb={2} fontFamily={webForm.styles.font_family}>
                                <h2>{webForm.name}</h2>
                                <span>{webForm.description}</span>
                            </Box>
                            <Box>
                                <Grid container spacing={2} rowSpacing={0.1}>
                                    {webForm.fields.map((item, index) => (
                                        <Grid item key={index} xs={12}>
                                            {item.client_id ? (
                                                <CustomField
                                                    customField={item}
                                                    onChange={
                                                        handleChangeCustomField
                                                    }
                                                    value={
                                                        formValues
                                                            ?.custom_field[
                                                            item.id
                                                        ]?.value
                                                    }
                                                    error={
                                                        formValues
                                                            ?.custom_field[
                                                            item.id
                                                        ]?.error
                                                    }
                                                    errorMessage={
                                                        formValues
                                                            ?.custom_field[
                                                            item.id
                                                        ]?.error &&
                                                        formValues
                                                            ?.custom_field[
                                                            item.id
                                                        ]?.errorMessage
                                                    }
                                                    styles={webForm.styles}
                                                />
                                            ) : (
                                                <>
                                                    {item.name === "phone" ? (
                                                        <MuiTelInput
                                                            name={item.name}
                                                            label={t(
                                                                item.label
                                                            )}
                                                            defaultCountry={getCountryCode()}
                                                            forceCallingCode
                                                            langOfCountryName={i18n.language}
                                                            value={
                                                                formValues[
                                                                    item.name
                                                                ]?.value
                                                            }
                                                            error={
                                                                formValues[
                                                                    item.name
                                                                ]?.error
                                                            }
                                                            helperText={
                                                                formValues[
                                                                    item.name
                                                                ]?.error &&
                                                                formValues[
                                                                    item.name
                                                                ]?.errorMessage
                                                            }
                                                            onChange={
                                                                handleChangePhone
                                                            }
                                                            variant="filled"
                                                            fullWidth
                                                            InputLabelProps={{
                                                                style: {
                                                                    color: webForm
                                                                        .styles
                                                                        .font_color,
                                                                    fontSize: `${webForm
                                                                        .styles
                                                                        .font_size ||
                                                                        "14"
                                                                        }px`,
                                                                    fontFamily:
                                                                        webForm
                                                                            .styles
                                                                            .font_family,
                                                                },
                                                            }}
                                                            InputProps={{
                                                                style: {
                                                                    borderRadius:
                                                                        webForm
                                                                            .styles
                                                                            .field_style ===
                                                                            "square"
                                                                            ? "0"
                                                                            : "8px",
                                                                    backgroundColor:
                                                                        "#fff",
                                                                    boxShadow:
                                                                        "none",
                                                                    borderColor:
                                                                        "#E0E2E4",
                                                                },
                                                            }}
                                                        />
                                                    ) : item.name === 'contact_source' ? (
                                                        <FormControl variant="filled" sx={{ margin: 0 }} fullWidth>
                                                            <InputLabel id={item.name}>{t(item.label)}</InputLabel>
                                                            <Select
                                                                label={t(item.label)}
                                                                labelId={item.id + item.name}
                                                                id={item.name}
                                                                name={item.name}
                                                                value={formValues[item.name]?.value || ''}
                                                                onChange={handleChange}
                                                            >
                                                                <MenuItem value={""}>
                                                                    <i>{t("Selecione")}</i>
                                                                </MenuItem>
                                                                {contactSources.map((contactSource) => (
                                                                    <MenuItem key={contactSource.id} value={contactSource.id}>
                                                                        {contactSource.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <TextField
                                                            name={item.name}
                                                            label={t(
                                                                item.label
                                                            )}
                                                            value={
                                                                formValues[
                                                                    item.name
                                                                ]?.value
                                                            }
                                                            error={
                                                                formValues[
                                                                    item.name
                                                                ]?.error
                                                            }
                                                            helperText={
                                                                formValues[
                                                                    item.name
                                                                ]?.error &&
                                                                formValues[
                                                                    item.name
                                                                ]?.errorMessage
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            variant="filled"
                                                            fullWidth
                                                            InputLabelProps={{
                                                                style: {
                                                                    color: webForm
                                                                        .styles
                                                                        .font_color,
                                                                    fontSize: `${webForm
                                                                        .styles
                                                                        .font_size ||
                                                                        "14"
                                                                        }px`,
                                                                    fontFamily:
                                                                        webForm
                                                                            .styles
                                                                            .font_family,
                                                                },
                                                            }}
                                                            InputProps={{
                                                                style: {
                                                                    borderRadius:
                                                                        webForm
                                                                            .styles
                                                                            .field_style ===
                                                                            "square"
                                                                            ? "0"
                                                                            : "8px",
                                                                    backgroundColor:
                                                                        "#fff",
                                                                    boxShadow:
                                                                        "none",
                                                                    borderColor:
                                                                        "#E0E2E4",
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </Grid>
                                    ))}
                                </Grid>
                                <Box>
                                    <div>
                                        <Button
                                            onClick={handleSave}
                                            variant="contained"
                                            disableElevation
                                            disabled={loading}
                                            fullWidth
                                            sx={{
                                                mt: 1,
                                                color: webForm.styles
                                                    .font_color,
                                                backgroundColor:
                                                    webForm.styles.button_color,
                                                "&:hover": {
                                                    backgroundColor:
                                                        webForm.styles
                                                            .button_color,
                                                },
                                                fontFamily:
                                                    webForm.styles.font_family,
                                            }}
                                        >
                                            {webForm.type === 'open_whatsapp_conversation' ? t("Enviar no Whatsapp") : t("Enviar")}
                                        </Button>
                                    </div>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        );
    }
};

export default WebFormsContent;
